/**
 * THE SHELL'S HAND ON THE §9 COLLECTOR.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS FILE EXISTS
 * ═══════════════════════════════════════════════════════════════════════════
 * `analytics-client.ts` shipped complete, tested and with ZERO CALLERS. Its own
 * header names the three calls the shell owes it, and no lane made them: the
 * router belonged to one agent, the collector to another, and the hand-off note
 * was the only thing joining them. The result was §9's whole client half —
 * `funnel_step`, `fps_bucket`, `desync_detected` — never produced, which in turn
 * made four of the six dashboards (the funnel, crash-free sessions, the frame
 * histogram and the desync early warning) query for rows nothing writes.
 *
 * This is the join. It is deliberately the ONLY place outside `engine/match/**`
 * that touches the collector, so "is analytics wired" is a question with one
 * file for an answer.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY IT LOADS THE COLLECTOR LAZILY
 * ═══════════════════════════════════════════════════════════════════════════
 * The SHELL chunk is measured against a 150 KB gz budget (ADR-0005) and stands
 * at 111.7. Telemetry must never be on the critical path of a first paint —
 * §6 puts a 90-second budget on a cold visitor reaching a played match, and a
 * collector that cannot collect anything until the server has answered
 * `GET /analytics/consent` has no reason to be parsed before the first frame.
 *
 * So this module — a route table and four thin functions, all of whose imports
 * are `import type` and therefore erased — is what the shell statically holds.
 * The collector itself arrives through `import()` after the first render.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * EVERY FUNCTION HERE IS FIRE-AND-FORGET, AND CANNOT THROW
 * ═══════════════════════════════════════════════════════════════════════════
 * A failed telemetry import, a rejected consent lookup and an offline device
 * are all the same thing to the player: nothing. Each entry point swallows its
 * own rejection, because the alternative is an unhandled rejection in the
 * router for data nobody reads in real time.
 */
import type { FunnelStep } from './analytics-events';

type Collector = typeof import('./analytics-client');

let modPromise: Promise<Collector> | null = null;
let started = false;
/**
 * Whether the SERVER said yes.
 *
 * The collector drops everything until its own `accepted` is set, so forwarding
 * after a decline would be harmless — but it would still mean a dynamic import,
 * a promise and a call per navigation for a session that will never send a
 * byte. Recording the answer here makes a declined session genuinely free, and
 * makes "collects nothing" observable at this seam rather than only inside the
 * object it delegates to.
 */
let collecting = false;
/** The last path a step was reported for, so a re-render is not a page view. */
let lastPath = '';

function load(): Promise<Collector> {
  modPromise ??= import('./analytics-client');
  return modPromise;
}

/**
 * The routes that ARE a funnel step.
 *
 * Deliberately partial. `queue_enter` and `match_enter` are NOT here: entering
 * `/arena` is not entering a queue (the arena opens on a menu, a training
 * difficulty or a lobby seat depending on what is pending), so those two are
 * reported from `engine/bootstrap.ts` where the distinction is actually known.
 * A router-level guess would have made the funnel's biggest drop-off — queue to
 * match — unmeasurable by overcounting its numerator.
 */
const ROUTE_STEPS: Readonly<Record<string, FunnelStep>> = Object.freeze({
  '/': 'landing_view',
  '/shop': 'shop_view',
});

/**
 * Start collecting, if the server says this account consented.
 *
 * Idempotent — the client is too, but returning early here also avoids a second
 * dynamic import on a shell that re-boots (the preview harness does).
 *
 * The route step for the page the player actually landed on is fired AFTER
 * `start()` resolves, because `funnel()` drops everything until consent is
 * known. Without the re-fire, the single most valuable step in the funnel —
 * `landing_view`, the denominator of every conversion rate below it — would be
 * dropped on every cold load and present on none.
 */
export function startShellAnalytics(path?: string): void {
  if (started) return;
  started = true;
  void load()
    .then(async (m) => {
      const ok = await m.analytics.start();
      if (!ok) return;
      collecting = true;
      lastPath = '';
      trackRoute(path ?? currentPath());
    })
    .catch(() => {
      /* telemetry is never allowed to break the shell */
    });
}

/** One funnel step, by name. Safe before `start()` — it is simply dropped. */
export function trackFunnel(step: FunnelStep, extra?: Record<string, unknown>): void {
  if (!collecting) return;
  void load()
    .then((m) => {
      m.analytics.funnel(step, extra ?? {});
    })
    .catch(() => {});
}

/**
 * Report a navigation, if this route is a funnel step.
 *
 * Deduplicated on the path: `render()` runs again whenever the identity guess
 * is corrected, the locale is adopted or a store the shell subscribes to
 * changes, and none of those is a page view.
 */
export function trackRoute(path: string): void {
  if (!collecting) return;
  const step = ROUTE_STEPS[path];
  if (step === undefined) return;
  if (path === lastPath) return;
  lastPath = path;
  trackFunnel(step);
}

/**
 * Stop collecting and discard the queue.
 *
 * Called from `signOut()`. Discarding rather than flushing is the collector's
 * own rule and the reason this is not just `analytics.stop()`: a sign-out must
 * not be followed by one last upload of the session that preceded it.
 * `started` is cleared so a subsequent sign-in starts a genuinely new session
 * with a new id, rather than resuming one that belonged to another account.
 */
export function stopShellAnalytics(): void {
  if (!started) return;
  started = false;
  collecting = false;
  lastPath = '';
  void load()
    .then((m) => {
      m.resetAnalytics();
    })
    .catch(() => {});
}

/**
 * §9's `desync_detected`, from the one place that can see one.
 *
 * The netcode records the facts on the session (`NetMatchSession.lastDesync`)
 * and never reports them itself: `engine/match/net/session.ts` is loaded by the
 * BACKEND's two-client suite, whose vitest config aliases `@rga/sim` and
 * `@rga/proto` and nothing else, so a `@/engine/analytics` import there would
 * resolve under Vite and fail under that runner.
 *
 * Sent immediately and off the queue by the collector — a fifteen-second batch
 * delay on a report from a client that is about to be closed in frustration is
 * a report that never arrives.
 */
export function reportDesync(report: {
  readonly matchId: string;
  readonly tick: number;
  readonly clientHash: number;
  readonly serverHash: number;
  readonly cardTableVersion: number;
  readonly protoVersion: number;
  readonly recovered?: boolean;
}): void {
  if (!collecting) return;
  void load()
    .then((m) =>
      m.analytics.desync({
        ...report,
        clientBuild: buildId(),
        recovered: report.recovered === true,
      }),
    )
    .catch(() => {});
}

/**
 * The build this bundle was cut from, for the desync row.
 *
 * `protoVersion` is deliberately NOT read here: it lives in `@rga/proto`, which
 * is match-layer code, and importing it would drag the wire protocol into the
 * SHELL chunk to stamp one integer. The netcode already knows the number and
 * passes it in.
 *
 * `VITE_BUILD_ID` is read through `import.meta.env`, so an unset variable is
 * `undefined` rather than a ReferenceError and the fallback is honest about
 * what it is instead of pretending to be a release.
 */
function buildId(): string {
  const v = import.meta.env.VITE_BUILD_ID as unknown;
  return typeof v === 'string' && v !== '' ? v.slice(0, 64) : 'dev';
}

function currentPath(): string {
  try {
    return globalThis.location?.pathname ?? '/';
  } catch {
    return '/';
  }
}

/** Test seam: forget the module handle and the started flag. */
export function resetShellAnalytics(): void {
  started = false;
  collecting = false;
  lastPath = '';
  modPromise = null;
}
